import { apiClient } from "./apiClient";
import { API_ENDPOINTS } from "../../src/constants";
import type { ApiResponse } from "@/types";
import { getToken, getUser } from "../lib/storage";

export interface Agent {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  status?: "ACTIVE" | "INACTIVE" | "BLOCKED";
  [key: string]: any;
}

export type CreateAgentDto = Omit<Agent, "id">;
export type UpdateAgentDto = Partial<CreateAgentDto>;

export interface ListParams {
  page?: number;
  limit?: number;
  isActive?: boolean;
  agentInstanceId?:string;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  search?: string;
  filter?: Record<string, any>;
  chatBotId?: string;
  status?: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export interface GenerateAgentIntentsPayload {
  name: string;
  description?: string;
  industry?: string;
}

export interface GeneratedAgentIntent {
  capabilityCode: string;
  description?: string;
  examples: string[];
}

function buildTenantHeaders(): Record<string, string> {
  const user = getUser();
  const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
  const lsTenantSlug = localStorage.getItem('agentic_tenant_slug') || localStorage.getItem('tenant_slug');
  const tenantId = lsTenantId || user?.tenantId;
  const tenantSlug = lsTenantSlug;
  const headers: Record<string, string> = {};
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  else if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
  return headers;
}

/* ---------- Helpers ---------- */
export const buildListQuery = (params?: ListParams) => {
  const qs = new URLSearchParams();

  if (!params) return qs.toString();
  if (params.page) qs.set("page", String(params.page));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortDir) qs.set("sortDir", params.sortDir);
  if (params.search) qs.set("search", params.search);
  if (params.agentInstanceId) qs.set("agentInstanceId", params.agentInstanceId);
  if(params.chatBotId) qs.set("chatBotId", params.chatBotId);
  if(params.includeDeleted) qs.set("includeDeleted", String(params.includeDeleted))
  if(params.status) qs.set("status", params.status);

  
  if (params.filter && Object.keys(params.filter).length) {
    qs.set("filter", JSON.stringify(params.filter));
  }

  return qs.toString();
};

/* ================================================================
   Agent Types Service
================================================================ */
export const agentService = {
  async listAgentTypes(params?: ListParams): Promise<Paginated<Agent>> {
    const query = buildListQuery(params);
    const url = query
      ? `${API_ENDPOINTS.AGENTS.LIST}?${query}`
      : API_ENDPOINTS.AGENTS.LIST;

    const result = await apiClient.get<ApiResponse<Paginated<Agent>>>(url, {
      authenticate: true,
    });
    return (result as any).data ?? (result as unknown as Paginated<Agent>);
  },

  async getAgentTypeById(id: string): Promise<Agent> {
    const result = await apiClient.get<ApiResponse<Agent>>(
      API_ENDPOINTS.AGENTS.DETAILS(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Agent);
  },

  async createAgentType(payload: CreateAgentDto): Promise<Agent> {
    const result = await apiClient.post<ApiResponse<Agent>>(
      API_ENDPOINTS.AGENTS.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Agent);
  },

  async updateAgentType(id: string, payload: UpdateAgentDto): Promise<Agent> {
    const result = await apiClient.patch<ApiResponse<Agent>>(
      API_ENDPOINTS.AGENTS.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as Agent);
  },

  async deleteAgentType(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.AGENTS.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as { success: boolean });
  },

  async generateAgentTypeIntents(
    payload: GenerateAgentIntentsPayload
  ): Promise<{ capabilities: GeneratedAgentIntent[] }> {
    const result = await apiClient.post<ApiResponse<{ capabilities: GeneratedAgentIntent[] }>>(
      API_ENDPOINTS.AGENTS.GENERATE_INTENTS,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? (result as unknown as { capabilities: GeneratedAgentIntent[] });
  },
};

/* ================================================================
   Agent Instances Service
================================================================ */
export const agentInstanceService = {
  async createAgentInstance(payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async getAgentInstanceById(id: string, params: ListParams): Promise<any> {
    const query = buildListQuery(params);
    const url = query
      ? `${API_ENDPOINTS.AGENT_INSTANCES.GET_BY_ID(encodeURIComponent(id))}?${query}`
      : API_ENDPOINTS.AGENT_INSTANCES.GET_BY_ID(encodeURIComponent(id));
      
    const result = await apiClient.get<ApiResponse<any>>(url,{ 
      authenticate: true });
    return (result as any).data ?? result;
  },

  async listAgentInstance(params?: ListParams): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const url = query
      ? `${API_ENDPOINTS.AGENT_INSTANCES.LIST}?${query}`
      : API_ENDPOINTS.AGENT_INSTANCES.LIST;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });
    return (result as any).data ?? result;
  },

  async updateAgentInstance(id: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.patch<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async deleteAgentInstance(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.AGENT_INSTANCES.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async restore(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.post<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.AGENT_INSTANCES.RESTORE(encodeURIComponent(id)),
      {},
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async updateAgentInstanceAiSettings(id: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.put<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.UPDATEAISETTINGS(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async getElevenLabsVoices(): Promise<{ voices: Array<{ voice_id: string; name: string }>; warning?: string }> {
    const result = await apiClient.get<{ voices: Array<{ voice_id: string; name: string }> }>(
      API_ENDPOINTS.VOICE.ELEVENLABS_VOICES,
      { authenticate: true }
    );
    return result as { voices: Array<{ voice_id: string; name: string }>; warning?: string };
  },

  async getFishVoices(): Promise<{ voices: Array<{ id: string; name: string; languages: string[] }>; warning?: string }> {
    const result = await apiClient.get<{ voices: Array<{ id: string; name: string; languages: string[] }> }>(
      API_ENDPOINTS.VOICE.FISH_VOICES,
      { authenticate: true }
    );
    return result as { voices: Array<{ id: string; name: string; languages: string[] }>; warning?: string };
  },

  async previewElevenLabsVoice(params: { voiceId: string; text: string; provider?: string }): Promise<Blob> {
    const token = getToken();
    // Use the configured API base URL (e.g. http://localhost:9060) so the request
    // reaches the API gateway, not the CRA dev server which has no proxy for /core/.
    const apiBase = ((process.env.REACT_APP_API_URL ?? process.env.REACT_APP_API_BASE_URL) || '').replace(/\/+$/, '');
    const url = new URL(
      apiBase ? `${apiBase}${API_ENDPOINTS.VOICE.ELEVENLABS_TTS}` : API_ENDPOINTS.VOICE.ELEVENLABS_TTS,
      window.location.origin,
    );
    url.searchParams.set('voiceId', params.voiceId);
    url.searchParams.set('text', params.text);
    if (params.provider) url.searchParams.set('provider', params.provider);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...buildTenantHeaders(),
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const body = await response.text();
        if (body) errorMessage = body;
      } catch {
        // ignore body parsing failures
      }
      throw new Error(errorMessage);
    }

    // Use arrayBuffer + explicit MIME type so the blob is always audio/mpeg
    // regardless of whether the nginx proxy preserves the Content-Type header.
    const buffer = await response.arrayBuffer();
    return new Blob([buffer], { type: 'audio/mpeg' });
  },


  async listDepartment(params?: ListParams): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const url = query
      ? `${API_ENDPOINTS.AGENT_INSTANCES.LIST}?${query}`
      : API_ENDPOINTS.DEPARTMENT.LIST;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });
    return (result as any).data ?? result;
  },


 
  

  async AgentInstanceDocument(id: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.UPLOADS.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
  

  async listAgentInstanceDocuments(
    id: string,
    params?: ListParams
  ): Promise<any> {
    const query = buildListQuery(params);
    const path = API_ENDPOINTS.AGENT_INSTANCES.DOCUMENTS.LIST(
      encodeURIComponent(id)
    );
    const url = query ? `${path}?${query}` : path;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });

    return (result as any).data ?? (result as unknown as Paginated<any>);
  },

  /**
   * Train document - Link to agent instance and update metadata
   * Uses existing train-document endpoint
   */
  async trainDocument(
    agentInstanceId: string,
    documentId: string
  ): Promise<any> {
    try {
      const response = await apiClient.post<ApiResponse<any>>(
        API_ENDPOINTS.AGENT_INSTANCES.DOCUMENTS.TRAIN_DOCUMENT(
          encodeURIComponent(agentInstanceId),
          encodeURIComponent(documentId)
        ),
        {},
        { authenticate: true }
      );
      return (response as any).data ?? response;
    } catch (error: any) {
      console.error('Failed to train document:', error);
      throw error;
    }
  },

  /**
   * Delete KB document
   * Uses existing documents delete endpoint
   */
  async deleteKBDocument(
    agentInstanceId: string,
    documentId: string
  ): Promise<any> {
    try {
      const response = await apiClient.delete<ApiResponse<any>>(
        API_ENDPOINTS.AGENT_INSTANCES.DOCUMENTS.DELETE_DOCUMENT(
          encodeURIComponent(agentInstanceId),
          encodeURIComponent(documentId)
        ),
        { authenticate: true }
      );
      return (response as any).data ?? response;
    } catch (error: any) {
      console.error('Failed to delete KB document:', error);
      throw error;
    }
  },
  


  async createQA(payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.QA.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
  async listQA(params?: ListParams): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const url = query ? `${API_ENDPOINTS.QA.LIST}?${query}` : API_ENDPOINTS.QA.LIST;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });
    return (result as any).data ?? result;
  },

  async updateQA(id: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.patch<ApiResponse<any>>(
      API_ENDPOINTS.QA.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
  async trainQA(payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.QA.TRAIN,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
  async deleteQA(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.QA.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },


  async createConversationAudit(payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.CONAUDIT.CREATE,
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },
  async listConversationAudits(params?: any): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const url = query ? `${API_ENDPOINTS.CONAUDIT.LIST}?${query}` : API_ENDPOINTS.QA.LIST;

    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, {
      authenticate: true,
    });
    return (result as any).data ?? result;
  },

  async updateConversationAudit(id: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.patch<ApiResponse<any>>(
      API_ENDPOINTS.CONAUDIT.UPDATE(encodeURIComponent(id)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async deleteConversationAudit(id: string): Promise<{ success: boolean }> {
    const result = await apiClient.delete<ApiResponse<{ success: boolean }>>(
      API_ENDPOINTS.CONAUDIT.DELETE(encodeURIComponent(id)),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  /**
   * agent instance actions
   */
  async listAgentActions(agentInstanceId: string, params?: ListParams): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const path = API_ENDPOINTS.AGENT_INSTANCES.ACTIONS.LIST(encodeURIComponent(agentInstanceId));
    const url = query ? `${path}?${query}` : path;
    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, { authenticate: true });
    return (result as any).data ?? result;
  },

  async getAgentAction(agentInstanceId: string, actionId: string): Promise<any> {
    const result = await apiClient.get<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.ACTIONS.GET(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(actionId)
      ),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async createAgentAction(agentInstanceId: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.ACTIONS.CREATE(encodeURIComponent(agentInstanceId)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async updateAgentAction(
    agentInstanceId: string,
    actionId: string,
    payload: Record<string, any>
  ): Promise<any> {
    const result = await apiClient.patch<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.ACTIONS.UPDATE(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(actionId)
      ),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async deleteAgentAction(agentInstanceId: string, actionId: string): Promise<any> {
    const result = await apiClient.delete<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.ACTIONS.DELETE(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(actionId)
      ),
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async restoreAgentAction(agentInstanceId: string, actionId: string): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.ACTIONS.RESTORE(
        encodeURIComponent(agentInstanceId),
        encodeURIComponent(actionId)
      ),
      {},
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async createAgentIntegration(agentInstanceId: string, payload: Record<string, any>): Promise<any> {
    const result = await apiClient.post<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.INTEGRATION.CREATE(encodeURIComponent(agentInstanceId)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async updateAgentIntegration(agentInstanceId: string, integrationId: string,  payload: Record<string, any>): Promise<any> {
    const result = await apiClient.patch<ApiResponse<any>>(
      API_ENDPOINTS.AGENT_INSTANCES.INTEGRATION.UPDATE(encodeURIComponent(agentInstanceId), encodeURIComponent(integrationId)),
      payload,
      { authenticate: true }
    );
    return (result as any).data ?? result;
  },

  async listAgentIntegrations(agentInstanceId: string, params?: ListParams): Promise<Paginated<any>> {
    const query = buildListQuery(params);
    const path = API_ENDPOINTS.AGENT_INSTANCES.INTEGRATION.LIST(encodeURIComponent(agentInstanceId));
    const url = query ? `${path}?${query}` : path;
    const result = await apiClient.get<ApiResponse<Paginated<any>>>(url, { authenticate: true });
    return (result as any).data ?? result;
  },
};
